import _ from 'lodash';
import moment from 'moment-timezone';

import {
  ALL_AD_CRITERIA,
  ALL_CONTENT_CRITERIA,
  ALL_CRITERIA,
  CONTENT_VIEWERSHIP_CRITERIA,
  DEMOGRAPHIC_PANEL_CRITERIA,
  FEED_TYPE,
  FEED_VERSION_TYPE,
  GEO_WEIGHT_CRITERIA,
  GROUPED_VALUES,
  INT_SPOT_EXPOSURE,
  SelectOptionType,
  SPOT_EXPOSURE_CRITERIA,
  TIVO_X_REF_CRITERIA,
  US_SPOT_EXPOSURE,
  VIEWERSHIP_CRITERIA,
  X_REF_NON_US_CRITERIA,
  X_REF_US_CRITERIA,
  ALL_VIEWERSHIP_CRITERIA,
  ALL_VIEWERSHIP_AD_CRITERIA,
  ALL_VIEWERSHIP_CONTENT_CRITERIA,
  TIVO_ALL_VIEWERSHIP_CRITERIA,
  TIVO_ALL_VIEWERSHIP_AD_CRITERIA,
  TIVO_ALL_VIEWERSHIP_CONTENT_CRITERIA,
  STREAMING_GEO_PANEL_CRITERIA
 } from '../constants';

/**
 * Formats a string representation of a number to be comma separated
 * @param numStr {string} String representation of a number to be formatted
 * @return {string} comma separated string
 */
export const formatNum = (numStr: string) => {
  if (!numStr) return "--";

  return parseInt(numStr).toLocaleString();
};

/**
 * Formats a string representation of a date to be in the format MM.DD.YY
 * @param dateStr {string} String representation of a date to be formatted
 * @return {string} formatted date (MM.DD.YY) string
 */
export const formatDate = (dateString: any) => {
  if (!dateString) return "--";

  const date = new Date(dateString);
  // @ts-ignore
  if (isNaN(date)) return "--";

  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();

  // The month starts at 0 instead of 1, so we must offset it
  // The year returns as 2019, so we must slice the first two digits
  return `${month + 1}.${day}.${year.toString().slice(2)}`;
};

/**
 * Truncates a string based of of max characters
 * @param str {string} String that we may want to truncate
 * @param maxChars {int} Maximum characters to show including truncate (...)
 * @return {string} truncated string
 */
export const truncate = (str: string, maxChars: number) => {
  if (!maxChars) return str;

  if (str.length <= maxChars) {
    return str;
  }

  // We'll truncate the str since it's too long
  // We subtract 3 from the maxChars to leave room for "..."
  return str.slice(0, maxChars - 3) + "...";
};

/**
 * Returns the start of the current UTC day. We don't want to have to worry
 * about timezone conversion so we just send the beginning of the UTC day.
 * @return {moment} start of current UTC day
 */

export const getStartOfToday = () => moment.utc().startOf("day");

const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const validateEmail = (email: string) => {
  return EMAIL_REGEX.test(String(email).toLowerCase());
};

export const concatAccordingToCountry = (country: string, criteria: any[], US = US_SPOT_EXPOSURE, NON_US = INT_SPOT_EXPOSURE) => {


  return _.uniq(criteria.concat(
    country === "US" ? US : NON_US
  ));
};

export const getPbsValue = (pbse_version: string | SelectOptionType<FEED_VERSION_TYPE>): string => {
  const val = (typeof pbse_version === "string")
    ? pbse_version
    : _.get(pbse_version, 'value')
  return val || ''
}

function removeItemFromArray(array: string[], item: string) {
  return array.filter((i) => i !== item);
}

function getCriteriaWithRegionRules(criteriaIn: string[], country: string) {
  let criteria = [...criteriaIn]

  // region is only in US and AU
  if (country !== 'US' && country !== 'AU') {
    criteria = removeItemFromArray(criteria, 'region')
  }

  // region_name is only in US
  if (country !== 'US') {
    criteria = removeItemFromArray(criteria, 'region_name')
  }

  // Subdivision should be in all markets except US
  if (country === 'US') {
    criteria = removeItemFromArray(criteria, 'subdivision')
  }

  return criteria
}

function getAllViewershipCriteria(version: string, country: string) {
  if (typeof version === 'string' && version.startsWith('2.')) {
    return getCriteriaWithRegionRules(ALL_VIEWERSHIP_CRITERIA, country)
  }
  return concatAccordingToCountry(country, ALL_CRITERIA)
}

function getAllViewershipAdCriteria(version: string, country: string) {
  if (typeof version === 'string' && version.startsWith('2.')) {
    return getCriteriaWithRegionRules(ALL_VIEWERSHIP_AD_CRITERIA, country)
  }
  return concatAccordingToCountry(country, ALL_AD_CRITERIA)  
}

function getAllViewershipContentCriteria(version: string, country: string) {
  if (typeof version === 'string' && version.startsWith('2.')) {
    return getCriteriaWithRegionRules(ALL_VIEWERSHIP_CONTENT_CRITERIA, country)
  }
  return concatAccordingToCountry(country, ALL_CONTENT_CRITERIA)
}

export const getCriteriaByFeedType = (type: FEED_TYPE, country: SelectOptionType, pbs: SelectOptionType<FEED_VERSION_TYPE>) => {

  const pbse_version = getPbsValue(pbs)

  let CRITERIAS;
  switch (type) {
    case "viewership":
      CRITERIAS = VIEWERSHIP_CRITERIA;
      break;
    case "tivo content viewership":
      CRITERIAS = concatAccordingToCountry(
        country.value,
        CONTENT_VIEWERSHIP_CRITERIA.filter((c) => c !== "application")
      );
      break;
    case "content viewership":
      CRITERIAS = concatAccordingToCountry(
        country.value,
        CONTENT_VIEWERSHIP_CRITERIA
      );
      break;
    case "geo weights":
      CRITERIAS = GEO_WEIGHT_CRITERIA;
      break;
    case "demographic panel":
      CRITERIAS = DEMOGRAPHIC_PANEL_CRITERIA;
      break;
    case "x_ref":
      CRITERIAS =
        country.value === "US" ? X_REF_US_CRITERIA : X_REF_NON_US_CRITERIA;
      break;
    case "tivo x_ref":
      CRITERIAS = TIVO_X_REF_CRITERIA;
      break;
    case "tivo+acr x_ref":
      CRITERIAS = X_REF_US_CRITERIA;
      break;
    case 'all viewership':
      CRITERIAS = getAllViewershipCriteria(pbse_version, country.value)
      break;
    case 'all viewership ad':
      CRITERIAS = getAllViewershipAdCriteria(pbse_version, country.value)
      break;
    case 'all viewership content':
      CRITERIAS = getAllViewershipContentCriteria(pbse_version, country.value)
      break;
    case 'tivo all viewership':
      CRITERIAS = getCriteriaWithRegionRules(TIVO_ALL_VIEWERSHIP_CRITERIA, country.value)
      break;
    case 'tivo all viewership ad':
      CRITERIAS = getCriteriaWithRegionRules(TIVO_ALL_VIEWERSHIP_AD_CRITERIA, country.value)
      break;
    case 'tivo all viewership content':
      CRITERIAS = getCriteriaWithRegionRules(TIVO_ALL_VIEWERSHIP_CONTENT_CRITERIA, country.value)
      break;
    case 'streaming geo panel':
      CRITERIAS = STREAMING_GEO_PANEL_CRITERIA
      break;
    default:
      CRITERIAS = concatAccordingToCountry(
        country.value,
        SPOT_EXPOSURE_CRITERIA
      );
      break;
  }

  if ((type === "content viewership" || type === "spot exposure") && pbse_version === "1.7.0") {
    if (type === 'content viewership') {
      CRITERIAS = CRITERIAS.concat([
        GROUPED_VALUES.DEVICE_INPUT_CV,
        'app_id',
        'application_availability',
      ])
    }

    if (type === 'spot exposure') {
      CRITERIAS = CRITERIAS.concat([
        GROUPED_VALUES.DEVICE_INPUT_SE,
        'content_type',
        'application',
        'app_id',
        'application_availability',
        'audience_scope',
        'ad_source',
      ])
    }
  }

  // https://sambatv.atlassian.net/browse/SADL-2554
  if (pbse_version.startsWith('1.') && ['GB', 'AU'].includes(country.value) &&
      ((type === 'spot exposure' && pbse_version.includes('1.6.')) ||
      (type === 'content viewership' && pbse_version.includes('1.6.')) ||
      ['all viewership', 'all viewership content', 'all viewership ad'].includes(type))
    ) {
    CRITERIAS = [...CRITERIAS, 'extended_postal_code']
  }

  if (['all viewership', 'all viewership content'].includes(type)) {
    //https://sambatv.atlassian.net/browse/SADL-2071
    CRITERIAS = _.without(CRITERIAS, 'household_weight')
  }

  return CRITERIAS;
};

interface FeedFilter {
  type: string
  country: SelectOptionType<string>
  pbse_version: SelectOptionType<FEED_VERSION_TYPE>
}

/**
 * For configured report types, we search for version and country matches
 * If there is a '*' provided for country or version, we match all
 * It is up to server to make sure the validation does not have any 
 * configuration mistakes
 */
export const getMinDateWithReportConfig = (feed: FeedFilter, reportConfigs: any[]) => {
    
  const feedCountry = typeof feed.country === 'string'
    ? feed.country
    : feed.country.value;

  const reportConfig = reportConfigs.find((rc) => {
    if (rc.type !== feed.type) {
      return false
    }
    
    const countryEval = rc.country === '*' 
      ? true 
      : rc.country === feedCountry;
    
    const versionEval = rc.version === '*' 
      ? true 
      : rc.version === getPbsValue(feed.pbse_version)
    
    return countryEval && versionEval
  });  

  if (!reportConfig) {
    return {
      error: `Cant not find config for type: [${feed.type}] country: [${feedCountry}] version: [${feed.pbse_version && feed.pbse_version.value}]`,
      minDate: null
    }
  }

  const minDate = moment(reportConfig.config.min_date);
  const maxDate = moment().subtract(36, 'months');
  const finalDate = minDate.isBefore(maxDate) 
    ? maxDate 
    : minDate

  return {
    error: null,
    minDate: finalDate
  }
}




export const  getFeedMinAndMaxDates = (feed: any) => {
  const MINIMUM_START_DATE = moment(new Date("09/01/2018"));
  const MINIMUM_START_DATE_AU = moment(new Date("06/29/2020"));
  const MINIMUM_START_DATE_IT = moment(new Date("03/09/2021"));
  const MINIMUM_START_DATE_CA = moment(new Date("05/26/2021"));
  const MINIMUM_START_DATE_ES = moment(new Date("10/10/2021"));
  const MINIMUM_START_DATE_FR = moment(new Date("10/10/2021"));
  const MINIMUM_START_DATE_BVOD = moment(new Date("01/01/2022"));
  const MINIMUM_START_DATE_DEMOGRAPHIC_PANEL = moment(new Date("01/01/2022"));
  const MINIMUM_TIVO_CONTENT_VIEWERSHIP_DATE = moment(new Date("05/02/2019"));
  const MINIMUM_SAMBA_CONTENT_VIEWERSHIP_DATE = moment(new Date("01/01/2021"));

  const PBSVERSION144 = moment(new Date("01/09/2021"));
  const PBSVERSION145 = moment(new Date("01/01/2023"));

  const { service_start, country, pbse_version, type } = feed;
  const countryName = typeof country === "string" ? country : country.value;

  const pbseVersion = pbse_version
    ? typeof pbse_version === "string"
      ? pbse_version
      : pbse_version.value
    : null;

  let minStartDate;
  switch (countryName) {
    case "AU":
      minStartDate = MINIMUM_START_DATE_AU;
      break;
    case "IT":
      minStartDate = MINIMUM_START_DATE_IT;
      break;
    case "CA":
      minStartDate = MINIMUM_START_DATE_CA;
      break;
    case "ES":
      minStartDate = MINIMUM_START_DATE_ES;
      break;
    case "FR":
      minStartDate = MINIMUM_START_DATE_FR;
      break;

    default:
      minStartDate = MINIMUM_START_DATE;
      break;
  }

  let minEndDate = service_start
    ? moment(service_start)
    : countryName === "AU"
      ? MINIMUM_START_DATE_AU
      : countryName === "IT"
        ? MINIMUM_START_DATE_IT
        : countryName === "CA"
          ? MINIMUM_START_DATE_CA
          : countryName === "ES"
            ? MINIMUM_START_DATE_ES
            : countryName === "FR"
              ? MINIMUM_START_DATE_FR
              : MINIMUM_START_DATE;

  if (pbseVersion === "1.4.3") {
    minStartDate = MINIMUM_START_DATE_BVOD;
    minEndDate = MINIMUM_START_DATE_BVOD;
  } else if (pbseVersion === "1.4.4") {
    minStartDate = PBSVERSION144;
    minEndDate = PBSVERSION144;
  } else if (pbseVersion === "1.4.5") {
    minStartDate = PBSVERSION145;
    minEndDate = PBSVERSION145;
  }

  if (type === "demographic panel") {
    minStartDate = MINIMUM_START_DATE_DEMOGRAPHIC_PANEL;
  }

  if (type === "tivo content viewership") {
    minStartDate = MINIMUM_TIVO_CONTENT_VIEWERSHIP_DATE;
  }

  if (type === "content viewership") {
    minStartDate = MINIMUM_SAMBA_CONTENT_VIEWERSHIP_DATE;
  }

  return { minStartDate, minEndDate };
};
